import { ChevronDown } from 'lucide-react'
import { useCallback, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/utils'
import { IconCheck } from '@tabler/icons-react'

export function MetricSelect({
  onValueChange,
  defaultValue,
  filterTypes,
}: {
  onValueChange: (value: string) => void
  defaultValue: string
  filterTypes: { value: string; label: string }[]
}) {
  const [selectedValue, setSelectedValue] = useState(defaultValue)
  const [isOpen, setIsOpen] = useState(false)

  const handleValueChange = useCallback(
    (value: string) => {
      setSelectedValue(value)
      onValueChange(value)
      setIsOpen(false)
    },
    [onValueChange]
  )

  const getSelectedLabel = useCallback(() => {
    const selectedItem = filterTypes.find(
      (item) => item.value === selectedValue
    )
    return selectedItem?.label || 'Select metric'
  }, [selectedValue, filterTypes])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
  }, [])

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-[200px] justify-start h-8 font-normal"
              >
                <span className="truncate">{getSelectedLabel()}</span>
                <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[300px]">
            {getSelectedLabel()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[200px] p-2">
        <div className="max-h-[250px] overflow-y-auto">
          {filterTypes.map(({ value, label }, index) => (
            <CustomSelectionBox
              key={`${value}-${index}`}
              title={label}
              isSelected={value === selectedValue}
              onClick={() => handleValueChange(value)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

const CustomSelectionBox = ({
  title,
  isSelected,
  isDisabled = false,
  onClick,
}: {
  title: string
  isSelected: boolean
  isDisabled?: boolean
  onClick: () => void
}) => {
  return (
    <div
      className={cn(
        'flex h-10 items-center flex-1 relative cursor-pointer select-none rounded-sm py-1.5 pl-2 pr-7 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
        isDisabled && 'pointer-events-none opacity-50'
      )}
      onClick={() => !isDisabled && onClick()}
    >
      <span className={isSelected ? 'text-blue-600' : ''}>{title}</span>
      <span className="absolute right-2 flex h-5 w-5 items-center justify-center">
        {isSelected && <IconCheck className="text-blue-600" />}
      </span>
    </div>
  )
}
